import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Play, Square } from 'lucide-react';
import {
  fetchProcessingState,
  startProcessing,
  stopProcessing,
  updateProcessingQuota
} from '../../api';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const STATE_POLL_MS = 15000;

const SCOPE_LABELS = {
  all: 'Остановить всё',
  analysis: 'Только распознавание и анализ'
};

const SCOPE_HINTS = {
  all: 'Записи не скачиваются и не обрабатываются. Вебхуки Mango продолжают приниматься и копиться — задания выполнятся после старта.',
  analysis: 'Звонки продолжают скачиваться и копиться в очереди задач, но распознавание и LLM-анализ не запускаются.'
};

const toDateInputValue = (isoString) => {
  if (!isoString) {
    return '';
  }

  const parsed = new Date(isoString);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const formatMinutes = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : '—';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

const ProcessingControlCard = ({ user }) => {
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';

  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isSavingQuota, setIsSavingQuota] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopScope, setStopScope] = useState('analysis');

  // Поля формы держим отдельно от состояния с сервера: поллинг раз в 15 с не должен
  // затирать то, что оператор сейчас печатает.
  const [minutesLimitInput, setMinutesLimitInput] = useState('');
  const [periodStartInput, setPeriodStartInput] = useState('');
  const [autoStopScopeInput, setAutoStopScopeInput] = useState('analysis');
  const isQuotaFormTouched = useRef(false);

  const applyState = useCallback((next) => {
    setState(next);

    if (!isQuotaFormTouched.current) {
      setMinutesLimitInput(next?.minutesLimit != null ? String(next.minutesLimit) : '');
      setPeriodStartInput(toDateInputValue(next?.periodStartUtc));
      setAutoStopScopeInput(next?.autoStopScope === 'all' ? 'all' : 'analysis');
    }
  }, []);

  const load = useCallback(
    async ({ silent } = {}) => {
      try {
        const next = await fetchProcessingState();
        applyState(next);
      } catch (error) {
        if (!silent) {
          toast({
            title: 'Не удалось загрузить состояние обработки',
            description: describeError(error),
            variant: 'destructive'
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [applyState, toast]
  );

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ silent: true }), STATE_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const handleStart = async () => {
    setIsSwitching(true);
    try {
      const next = await startProcessing();
      applyState(next);
      toast({ title: 'Обработка запущена' });
    } catch (error) {
      toast({
        title: 'Не удалось запустить обработку',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const handleStop = async () => {
    setStopDialogOpen(false);
    setIsSwitching(true);
    try {
      const next = await stopProcessing(stopScope);
      applyState(next);
      toast({
        title: 'Обработка остановлена',
        description: SCOPE_HINTS[stopScope]
      });
    } catch (error) {
      toast({
        title: 'Не удалось остановить обработку',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const handleSaveQuota = async () => {
    const trimmedLimit = minutesLimitInput.trim();
    const parsedLimit = trimmedLimit === '' ? null : Number(trimmedLimit);

    if (parsedLimit !== null && (!Number.isFinite(parsedLimit) || parsedLimit < 0)) {
      toast({
        title: 'Некорректный лимит минут',
        description: 'Укажите целое неотрицательное число или оставьте поле пустым.',
        variant: 'destructive'
      });
      return;
    }

    setIsSavingQuota(true);
    try {
      const next = await updateProcessingQuota({
        minutesLimit: parsedLimit === null ? null : Math.round(parsedLimit),
        periodStartUtc: periodStartInput ? `${periodStartInput}T00:00:00Z` : null,
        autoStopScope: autoStopScopeInput
      });

      isQuotaFormTouched.current = false;
      applyState(next);
      toast({ title: 'Настройки лимита сохранены' });
    } catch (error) {
      toast({
        title: 'Не удалось сохранить лимит',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setIsSavingQuota(false);
    }
  };

  const renderStatusBadge = () => {
    if (isLoading) {
      return <Badge variant="outline">Загрузка…</Badge>;
    }

    if (!state) {
      return <Badge variant="destructive">Состояние недоступно</Badge>;
    }

    if (state.enabled) {
      return <Badge className="bg-emerald-600 hover:bg-emerald-600">Обработка идёт</Badge>;
    }

    if (state.stoppedReason === 'quota') {
      return <Badge variant="destructive">Остановлена: исчерпан лимит минут</Badge>;
    }

    return (
      <Badge variant="secondary">
        {state.stopScope === 'analysis' ? 'Остановлена: только анализ' : 'Остановлена полностью'}
      </Badge>
    );
  };

  const usedMinutes = state?.minutesUsed ?? 0;
  const limitMinutes = state?.minutesLimit ?? null;
  const usedPercent = limitMinutes ? Math.min(100, Math.round((usedMinutes / limitMinutes) * 100)) : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-3">
              Обработка звонков
              {renderStatusBadge()}
            </CardTitle>
            <CardDescription>
              Управление конвейером распознавания и анализа и порог по израсходованным минутам.
            </CardDescription>
          </div>

          {isAdmin && (
            <div className="flex gap-2">
              <Button data-ev="integrations.processing.start" onClick={handleStart} disabled={isSwitching || isLoading || state?.enabled}>
                <Play className="h-4 w-4 mr-2" />
                Старт
              </Button>
              <Button
                variant="outline"
                data-ev="integrations.processing.stop"
                onClick={() => setStopDialogOpen(true)}
                disabled={isSwitching || isLoading || !state?.enabled}
              >
                <Square className="h-4 w-4 mr-2" />
                Стоп
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {!state?.enabled && state && (
          <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{SCOPE_HINTS[state.stopScope] ?? SCOPE_HINTS.all}</span>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-400">Израсходовано минут</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatMinutes(usedMinutes)}
              {limitMinutes ? ` из ${formatMinutes(limitMinutes)}` : ' (лимит не задан)'}
            </span>
          </div>
          {usedPercent !== null && <Progress value={usedPercent} />}
          {limitMinutes != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Осталось {formatMinutes(state?.minutesRemaining ?? 0)} мин. Считаются завершённые задачи
              {state?.periodStartUtc ? ` с ${toDateInputValue(state.periodStartUtc)}` : ' за всё время'}.
            </p>
          )}
        </div>

        {isAdmin && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4 md:items-end">
            <div className="space-y-2">
              <Label htmlFor="processing-minutes-limit">Лимит минут</Label>
              <Input
                id="processing-minutes-limit"
                data-ev="integrations.processing.limit_minutes"
                type="number"
                min="0"
                step="1"
                placeholder="Без лимита"
                value={minutesLimitInput}
                onChange={(event) => {
                  isQuotaFormTouched.current = true;
                  setMinutesLimitInput(event.target.value);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="processing-period-start">Считать с</Label>
              <Input
                id="processing-period-start"
                data-ev="integrations.processing.period_start"
                type="date"
                value={periodStartInput}
                onChange={(event) => {
                  isQuotaFormTouched.current = true;
                  setPeriodStartInput(event.target.value);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="processing-auto-stop">При превышении лимита</Label>
              <Select
                trackId="integrations.processing.auto_stop_scope"
                trackValue
                value={autoStopScopeInput}
                onValueChange={(value) => {
                  isQuotaFormTouched.current = true;
                  setAutoStopScopeInput(value);
                }}
              >
                <SelectTrigger id="processing-auto-stop">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="analysis">{SCOPE_LABELS.analysis}</SelectItem>
                  <SelectItem value="all">{SCOPE_LABELS.all}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button data-ev="integrations.processing.quota_save" onClick={handleSaveQuota} disabled={isSavingQuota}>
              {isSavingQuota ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </div>
        )}
      </CardContent>

      <AlertDialog open={stopDialogOpen} onOpenChange={setStopDialogOpen}>
        <AlertDialogContent trackId="integrations.processing.stop_dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Что остановить?</AlertDialogTitle>
            <AlertDialogDescription>
              Уже поставленные задачи не теряются: они дождутся старта и будут обработаны.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <RadioGroup trackId="integrations.processing.stop_scope" trackValue value={stopScope} onValueChange={setStopScope} className="gap-4 py-2">
            {['analysis', 'all'].map((scope) => (
              <label key={scope} className="flex cursor-pointer items-start gap-3">
                <RadioGroupItem value={scope} id={`stop-scope-${scope}`} className="mt-1" />
                <span>
                  <span className="block text-sm font-medium">{SCOPE_LABELS[scope]}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{SCOPE_HINTS[scope]}</span>
                </span>
              </label>
            ))}
          </RadioGroup>

          <AlertDialogFooter>
            <AlertDialogCancel data-ev="integrations.processing.stop_cancel">Отмена</AlertDialogCancel>
            <Button variant="destructive" data-ev="integrations.processing.stop_confirm" onClick={handleStop}>
              Остановить
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ProcessingControlCard;
